import { BellRing, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import type { CardScope } from '../../credit'
import {
  BEFORE_CHOICES,
  OVERDUE_CHOICES,
  REMINDER_SETTINGS_PERM,
  useReminderSettings,
  useSaveReminderSettings,
  useSendReminderTest,
  type ReminderSettings,
} from '../../reminders'
import { toastError } from '../../ui'
import { Footer } from '../AccountForms'
import { CheckRow, Field, FormSection } from '../FormBits'

const EMAIL = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/

/** Hatırlatma ayarları düğmesi: şirkette yetkisi olana, kişiselde herkese (kendi ayarı). */
export function ReminderSettingsButton({ scope, size = 'sm' }: { scope: CardScope; size?: 'sm' | 'default' }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [open, setOpen] = useState(false)
  if (scope === 'company' && !can(REMINDER_SETTINGS_PERM)) return null
  return (
    <>
      <Button size={size} variant="outline" onClick={() => setOpen(true)}>
        <BellRing />
        {t('finance.reminders.button')}
      </Button>
      {open ? <ReminderSettingsModal scope={scope} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function ReminderSettingsModal({ scope, onClose }: { scope: CardScope; onClose: () => void }) {
  const { t } = useTranslation()
  const settings = useReminderSettings(scope)
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      icon={<BellRing />}
      title={t(scope === 'personal' ? 'finance.reminders.titlePersonal' : 'finance.reminders.titleCompany')}
      description={t(scope === 'personal' ? 'finance.reminders.hintPersonal' : 'finance.reminders.hintCompany')}
    >
      {settings.data ? (
        <ReminderForm scope={scope} initial={settings.data} onClose={onClose} />
      ) : (
        <Skeleton className="h-48" />
      )}
    </Modal>
  )
}

function DayChips({
  id,
  label,
  choices,
  value,
  onChange,
  format,
}: {
  id: string
  label: string
  choices: number[]
  value: number[]
  onChange: (v: number[]) => void
  format: (d: number) => string
}) {
  return (
    <fieldset className="space-y-2 sm:col-span-2" aria-labelledby={id}>
      <legend id={id} className="text-sm font-medium">
        {label}
      </legend>
      <div className="flex flex-wrap gap-1.5">
        {choices.map((d) => {
          const on = value.includes(d)
          return (
            <button
              key={d}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(on ? value.filter((x) => x !== d) : [...value, d])}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                on ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted',
              )}
            >
              {format(d)}
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}

function ReminderForm({
  scope,
  initial,
  onClose,
}: {
  scope: CardScope
  initial: ReminderSettings
  onClose: () => void
}) {
  const { t } = useTranslation()
  const save = useSaveReminderSettings(scope)
  const test = useSendReminderTest()
  const [v, setV] = useState(initial)
  const personal = scope === 'personal'
  const set = (patch: Partial<ReminderSettings>) => setV((x) => ({ ...x, ...patch }))
  const emailError =
    personal && v.emailTo.trim() && !EMAIL.test(v.emailTo.trim()) ? t('finance.reminders.emailInvalid') : ''
  const dirty = JSON.stringify(v) !== JSON.stringify(initial)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (emailError) return
        save.mutate(v, {
          onSuccess: () => {
            toast.success(t('finance.reminders.saved'))
            onClose()
          },
          onError: toastError,
        })
      }}
      className="space-y-5"
    >
      <FormSection>
        <div className="sm:col-span-2">
          <CheckRow checked={v.enabled} onChange={(e) => set({ enabled: e.target.checked })}>
            {t('finance.reminders.enabled')}
          </CheckRow>
        </div>
        <DayChips
          id="rm-before"
          label={t('finance.reminders.before')}
          choices={BEFORE_CHOICES}
          value={v.daysBefore}
          onChange={(daysBefore) => set({ daysBefore })}
          format={(d) => (d === 0 ? t('finance.reminders.dueDay') : t('finance.reminders.daysLeft', { count: d }))}
        />
        <DayChips
          id="rm-overdue"
          label={t('finance.reminders.overdue')}
          choices={OVERDUE_CHOICES}
          value={v.overdueDays}
          onChange={(overdueDays) => set({ overdueDays })}
          format={(d) => t('finance.reminders.daysLate', { count: d })}
        />
        <div className="sm:col-span-2">
          <CheckRow
            checked={v.emailEnabled}
            onChange={(e) => set({ emailEnabled: e.target.checked })}
            hint={t(personal ? 'finance.reminders.emailHintPersonal' : 'finance.reminders.emailHintCompany')}
          >
            {t('finance.reminders.email')}
          </CheckRow>
        </div>
        {personal && v.emailEnabled ? (
          <Field
            id="rm-to"
            label={t('finance.reminders.emailTo')}
            error={emailError || undefined}
            hint={t('finance.reminders.emailToHint', { email: initial.accountEmail || '—' })}
            full
          >
            <div className="flex flex-wrap gap-2">
              <Input
                id="rm-to"
                type="email"
                className="min-w-56 flex-1"
                placeholder={initial.accountEmail}
                value={v.emailTo}
                onChange={(e) => set({ emailTo: e.target.value })}
              />
              <Button
                type="button"
                variant="outline"
                disabled={dirty || test.isPending}
                title={dirty ? t('finance.reminders.saveFirst') : undefined}
                onClick={() =>
                  test.mutate(undefined, {
                    onSuccess: (to) => toast.success(t('finance.reminders.testSent', { email: to })),
                    onError: toastError,
                  })
                }
              >
                <Send />
                {t('finance.reminders.test')}
              </Button>
            </div>
          </Field>
        ) : null}
      </FormSection>
      <div className="flex justify-end gap-2 border-t pt-4">
        <Footer pending={save.isPending} disabled={!!emailError} onCancel={onClose} />
      </div>
    </form>
  )
}
